import { v4 } from 'uuid';

type Key = string | number | symbol;

const formatSizeUnits = (bytes: number): string => {
    let str: string;
    if (bytes >= 1073741824) {
        str = `${(bytes / 1073741824).toFixed(2)} GB`;
    } else if (bytes >= 1048576) {
        str = `${(bytes / 1048576).toFixed(2)} MB`;
    } else if (bytes >= 1024) {
        str = `${(bytes / 1024).toFixed(2)} KB`;
    } else if (bytes > 1) {
        str = `${bytes} bytes`;
    } else if (bytes === 1) {
        str = `${bytes} byte`;
    } else {
        str = '0 bytes';
    }
    return str;
};

export const getObjectApproximateMemorySize = (obj: any): string => {
    const size = new TextEncoder().encode(JSON.stringify(obj)).length;
    return formatSizeUnits(size);
};

interface SimpleMemoryCacheInterface<T> {
    cacheName: string;
    cache: Record<Key, T>;
    getCacheName: () => string;
    get: (key: Key) => T;
    getAll: () => Record<Key, T>;
    put: (key: Key, value: T) => void;
    delete: (key: Key) => void;
    reset: () => void;
    size: () => number;
}

// Simple MemoryCache. get/put data from/to an object. No TTL option
export class SimpleMemoryCache<T> implements SimpleMemoryCacheInterface<T> {
    cache: Record<Key, T>;
    cacheName: string;
    constructor(cacheName?: string) {
        this.cache = {};
        this.cacheName = cacheName || v4();
    }
    getCacheName() {
        return this.cacheName;
    }
    get(key: Key) {
        return this.cache[key];
    }
    getAll() {
        return this.cache;
    }
    put(key: Key, value: T) {
        this.cache[key] = value;
    }
    delete(key: Key) {
        delete this.cache[key];
    }
    reset() {
        this.cache = {};
    }
    size() {
        return Object.keys(this.cache).length;
    }
    approximateMemorySize() {
        return getObjectApproximateMemorySize(this.cache);
    }
}
